import type { ObservationType } from "./types.js";

/**
 * Tradução do export do Apple Health.
 *
 * ONDE ISTO RODA: no celular, dentro do PWA. Não no servidor.
 *
 * O plano gratuito do Cloudflare Workers dá 10 ms de CPU por requisição, e um
 * export do app Saúde passa fácil de 100 MB de XML. Não é questão de ser lento:
 * é impossível, e picotar não ajuda porque o limite é por requisição. O telefone
 * tem CPU de sobra e o Worker só recebe observações já normalizadas.
 *
 * De quebra, o XML bruto — que contém tudo que o app Saúde sabe dela, incluindo
 * o que a gente não pediu — nunca sai do aparelho. Era pra ser assim desde o
 * começo; o limite só apressou a decisão certa.
 *
 * Este módulo é a parte PURA: mapear tipos e converter um registro. A leitura
 * em streaming e o envio em lotes ficam em apps/web, onde há DOM.
 */

/** `type` do HealthKit → nosso tipo canônico. Fora desta tabela é ignorado. */
export const TIPOS_APPLE: Readonly<Record<string, ObservationType>> = {
  HKQuantityTypeIdentifierBodyMass: "weight",
  HKQuantityTypeIdentifierBodyFatPercentage: "body_fat",
  HKQuantityTypeIdentifierLeanBodyMass: "lean_mass",
  HKQuantityTypeIdentifierStepCount: "steps",
  HKQuantityTypeIdentifierRestingHeartRate: "hr_resting",
  HKQuantityTypeIdentifierHeartRateVariabilitySDNN: "hrv",
  HKQuantityTypeIdentifierVO2Max: "vo2max",
  HKQuantityTypeIdentifierBloodGlucose: "glucose",
  HKQuantityTypeIdentifierDietaryWater: "water",
  HKCategoryTypeIdentifierSleepAnalysis: "sleep",
};

/** Unidades do HealthKit que precisam de conversão para a nossa base SI. */
const CONVERSAO: Readonly<Record<string, { fator: number; unidade: string }>> = {
  lb: { fator: 0.45359237, unidade: "kg" },
  g: { fator: 0.001, unidade: "kg" },
  "mg/dL": { fator: 1, unidade: "mg/dL" },
  "mmol<180.1558800000541>/L": { fator: 18.0156, unidade: "mg/dL" },
  "fl_oz_us": { fator: 29.5735, unidade: "ml" },
  L: { fator: 1000, unidade: "ml" },
};

/** Um `<Record>` do XML, já com os atributos extraídos. */
export interface RegistroApple {
  type: string;
  sourceName?: string;
  unit?: string;
  startDate: string;
  endDate?: string;
  value?: string;
}

export interface ObservacaoNormalizada {
  ts: string;
  type: ObservationType;
  value: number | null;
  unit: string | null;
  payload: unknown;
  sourceId: string;
}

/**
 * Converte a data do Apple Health, que vem como "2026-09-08 07:12:33 -0300" —
 * espaço no lugar do T e offset sem dois-pontos. `Date.parse` não aceita.
 */
export function parseDataApple(texto: string): string | null {
  const m = /^(\d{4}-\d{2}-\d{2}) (\d{2}:\d{2}:\d{2}) ([+-])(\d{2})(\d{2})$/.exec(texto.trim());
  if (!m) {
    const solto = Date.parse(texto);
    return Number.isFinite(solto) ? new Date(solto).toISOString() : null;
  }
  const [, data, hora, sinal, hh, mm] = m;
  const iso = `${data}T${hora}${sinal}${hh}:${mm}`;
  const ms = Date.parse(iso);
  return Number.isFinite(ms) ? new Date(ms).toISOString() : null;
}

/**
 * Estágios de sono do HealthKit. `InBed` é tempo na cama, não sono — somá-lo
 * junto inflaria o total e o readiness passaria a elogiar quem só ficou deitado.
 */
const SONO_DORMINDO = new Set([
  "HKCategoryValueSleepAnalysisAsleepUnspecified",
  "HKCategoryValueSleepAnalysisAsleepCore",
  "HKCategoryValueSleepAnalysisAsleepDeep",
  "HKCategoryValueSleepAnalysisAsleepREM",
]);

export function normalizarRegistro(r: RegistroApple): ObservacaoNormalizada | null {
  const tipo = TIPOS_APPLE[r.type];
  if (!tipo) return null;

  const ts = parseDataApple(r.startDate);
  if (!ts) return null;

  // Identidade estável: reenviar o mesmo export não duplica. Inclui a fonte
  // porque relógio e celular registram passos no mesmo instante.
  const sourceId = `${r.type}|${r.startDate}|${r.sourceName ?? "?"}`;

  if (tipo === "sleep") {
    if (!r.value || !SONO_DORMINDO.has(r.value)) return null;
    const fim = r.endDate ? parseDataApple(r.endDate) : null;
    if (!fim) return null;
    const minutos = Math.round((Date.parse(fim) - Date.parse(ts)) / 60_000);
    if (minutos <= 0) return null;
    return {
      ts,
      type: "sleep",
      value: minutos,
      unit: "min",
      payload: { totalMinutes: minutos, bedtime: ts, wakeTime: fim },
      sourceId,
    };
  }

  // `Number("")` é 0, não NaN. Sem esta guarda, um registro sem valor viraria
  // "pesou 0 kg" e entraria na tendência como se fosse medida real.
  if (r.value === undefined || r.value.trim() === "") return null;
  const bruto = Number(r.value);
  if (!Number.isFinite(bruto)) return null;

  const conv = r.unit ? CONVERSAO[r.unit] : undefined;
  const valor = conv ? bruto * conv.fator : bruto;
  const unidade = conv ? conv.unidade : (r.unit ?? null);

  return {
    ts,
    type: tipo,
    value: valor,
    unit: unidade,
    payload: r.sourceName ? { device: r.sourceName } : null,
    sourceId,
  };
}

/**
 * Passos vêm em centenas de fragmentos por dia (um por intervalo de alguns
 * minutos, por aparelho). Mandar cada fragmento estouraria a cota de escrita do
 * D1 e não serve para nada — o rollup só quer o total do dia.
 *
 * Somamos por dia e por fonte, e o rollup depois pega o MÁXIMO entre fontes,
 * que é o comportamento correto para contador cumulativo.
 */
export function agregarPassosPorDia(
  registros: readonly ObservacaoNormalizada[],
  timeZone: string,
): ObservacaoNormalizada[] {
  const porChave = new Map<string, { total: number; ts: string; fonte: string }>();

  for (const r of registros) {
    if (r.type !== "steps" || r.value == null) continue;
    const dia = new Intl.DateTimeFormat("en-CA", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(new Date(r.ts));
    const fonte = (r.payload as { device?: string } | null)?.device ?? "?";
    const chave = `${dia}|${fonte}`;

    const atual = porChave.get(chave);
    if (atual) atual.total += r.value;
    else porChave.set(chave, { total: r.value, ts: `${dia}T12:00:00.000Z`, fonte });
  }

  return [...porChave.entries()].map(([chave, v]) => ({
    ts: v.ts,
    type: "steps" as const,
    value: Math.round(v.total),
    unit: "count",
    payload: { device: v.fonte },
    sourceId: `steps-dia|${chave}`,
  }));
}

/* ───────────────────── Varredura incremental do XML ───────────────────── */

/**
 * Por que NÃO um DOMParser.
 *
 * O `DOMParser` do navegador é tudo-ou-nada: exige o documento inteiro em
 * memória e só então devolve a árvore. Um export do app Saúde com anos de
 * histórico passa de 100 MB de texto, e o Safari de um iPhone mata a aba antes
 * disso. Como só precisamos dos atributos de `<Record>`, uma varredura por
 * pedaços resolve com memória constante.
 *
 * O preço é ter que respeitar aspas na mão: `>` é caractere legal DENTRO de um
 * valor de atributo em XML, sem precisar de escape. Procurar o primeiro `>`
 * depois de `<Record` cortaria a tag no meio e perderia o registro em silêncio —
 * exatamente o tipo de bug que só aparece em um dia esquisito de dado.
 */

/** Quanto de cauda guardar quando não há `<Record` nenhum no pedaço. */
const CAUDA = "<Record".length;

const ENTIDADES: Readonly<Record<string, string>> = {
  amp: "&",
  lt: "<",
  gt: ">",
  quot: '"',
  apos: "'",
};

function desescapar(texto: string): string {
  if (!texto.includes("&")) return texto;
  return texto.replace(/&(#x?[0-9a-fA-F]+|[a-zA-Z]+);/g, (todo, corpo: string) => {
    if (corpo.startsWith("#x") || corpo.startsWith("#X")) {
      const n = Number.parseInt(corpo.slice(2), 16);
      return Number.isFinite(n) ? String.fromCodePoint(n) : todo;
    }
    if (corpo.startsWith("#")) {
      const n = Number.parseInt(corpo.slice(1), 10);
      return Number.isFinite(n) ? String.fromCodePoint(n) : todo;
    }
    return ENTIDADES[corpo] ?? todo;
  });
}

/** Lê os atributos de uma tag de abertura já delimitada. */
function lerAtributos(tag: string): Record<string, string> {
  const attrs: Record<string, string> = {};
  const re = /([A-Za-z_][\w.:-]*)\s*=\s*("([^"]*)"|'([^']*)')/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(tag)) !== null) {
    const nome = m[1];
    if (nome) attrs[nome] = desescapar(m[3] ?? m[4] ?? "");
  }
  return attrs;
}

export interface ResultadoVarredura {
  registros: RegistroApple[];
  /**
   * O que sobrou sem fechar. Quem chama devolve isto na frente do próximo
   * pedaço — é o que impede um `<Record` partido no meio de sumir.
   */
  resto: string;
}

/**
 * Extrai os `<Record>` de um pedaço de XML. Sem estado interno: o estado é o
 * `resto`, que o chamador reinjeta. Isso mantém a função pura e testável fora
 * do navegador.
 */
export function varrerRegistros(pedaco: string): ResultadoVarredura {
  const registros: RegistroApple[] = [];
  let i = 0;

  for (;;) {
    const inicio = pedaco.indexOf("<Record", i);
    if (inicio === -1) {
      // Guarda a cauda: `<Reco` pode ter ficado no fim do pedaço anterior.
      const corte = Math.max(i, pedaco.length - CAUDA);
      return { registros, resto: pedaco.slice(corte) };
    }

    // `<Records` não é `<Record`. O caractere seguinte tem que ser separador.
    const depois = pedaco[inicio + CAUDA];
    if (depois !== undefined && !/[\s/>]/.test(depois)) {
      i = inicio + CAUDA;
      continue;
    }

    let j = inicio + CAUDA;
    let aspas: '"' | "'" | null = null;
    let fim = -1;
    for (; j < pedaco.length; j++) {
      const ch = pedaco[j];
      if (aspas) {
        if (ch === aspas) aspas = null;
      } else if (ch === '"' || ch === "'") {
        aspas = ch;
      } else if (ch === ">") {
        fim = j;
        break;
      }
    }

    // Tag cortada pelo fim do pedaço: devolve inteira para a próxima rodada.
    if (fim === -1) return { registros, resto: pedaco.slice(inicio) };

    const attrs = lerAtributos(pedaco.slice(inicio + CAUDA, fim));
    if (attrs.type && attrs.startDate) {
      registros.push({
        type: attrs.type,
        sourceName: attrs.sourceName,
        unit: attrs.unit,
        startDate: attrs.startDate,
        endDate: attrs.endDate,
        value: attrs.value,
      });
    }
    i = fim + 1;
  }
}
